/**
 * KS2-I3 — Sentinel Page with Drift Event Resolution
 *
 * Sentinel may: identify, compare, classify, rank, explain, route to review.
 * Sentinel may NOT: mutate source state, mutate canon, approve/reject versions,
 * invoke SAFE-HOLD autonomously, activate SUPERSEDED, accept evidence,
 * or alter institutional authority.
 *
 * Resolution is HUMAN-INITIATED (preview identity — KS-GATE-AUTH open).
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

const EVENT_TYPE_COLORS = {
  ADDED_INFORMATION:   'bg-blue-950 text-blue-400 border border-blue-900',
  REMOVED_INFORMATION: 'bg-red-950 text-red-400 border border-red-900',
  MODIFIED_INFORMATION:'bg-amber-950 text-amber-400 border border-amber-900',
  POSSIBLE_DRIFT:      'bg-orange-950 text-orange-400 border border-orange-900',
  CONFLICT:            'bg-rose-950 text-rose-400 border border-rose-900',
  LOST_CONSTRAINT:     'bg-red-950 text-red-300 border border-red-800',
  POSSIBLE_DUPLICATE:  'bg-zinc-800 text-zinc-400 border border-zinc-700',
  POSSIBLE_CONFLICT:   'bg-yellow-950 text-yellow-400 border border-yellow-900',
};

const PRIORITY_COLORS = {
  HIGH:   'text-rose-400',
  MEDIUM: 'text-amber-400',
  LOW:    'text-zinc-500',
};

const RESOLUTION_STATE_OPTIONS = [
  { value: 'RESOLVED',  label: 'Resolved — no further action needed' },
  { value: 'HELD',      label: 'Hold — requires further investigation' },
  { value: 'ESCALATED', label: 'Escalate — route to senior reviewer' },
];

// ─── Drift event resolution form (KS2-I3) ────────────────────────────────────

function ResolutionForm({ eventId, currentReviewState, onResolved }) {
  const [reviewer,         setReviewer]         = useState('');
  const [note,             setNote]             = useState('');
  const [resolutionState,  setResolutionState]  = useState('RESOLVED');
  const [status,           setStatus]           = useState('idle');
  const [errMsg,           setErrMsg]           = useState('');
  const [result,           setResult]           = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!reviewer.trim()) { setErrMsg('Reviewer identity is required.'); return; }
    if (!note.trim())     { setErrMsg('A resolution note is required.'); return; }
    setStatus('loading'); setErrMsg('');
    try {
      const r = await api.post(`/sentinel/drift-events/${eventId}/resolve`, {
        reviewer, note, resolution_state: resolutionState,
      });
      setResult(r.data);
      setStatus('success');
      if (onResolved) onResolved(r.data);
    } catch (err) {
      setErrMsg(err.response?.data?.detail || 'Resolution failed.');
      setStatus('idle');
    }
  }

  if (status === 'success') {
    return (
      <div
        className="rounded p-3 border border-emerald-900 text-xs text-emerald-400 bg-emerald-950"
        data-testid="resolution-success"
      >
        <p className="font-semibold">
          Resolved: {result?.prior_state} → {result?.resulting_state}
        </p>
        <p className="mt-1 text-emerald-600">
          By: {result?.resolved_by} — {result?.note}
        </p>
        <p className="mt-1 text-zinc-600 italic">{result?.governance_note}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2 pt-2 border-t border-zinc-800" data-testid="resolution-form">
      <p className="text-xs text-zinc-600 italic">
        Human-initiated resolution. Reviewer identity is preview-supplied (KS-GATE-AUTH open).
        Resolution applies to this analytical event only — does NOT mutate source, canon, SUPERSEDED, or SAFE-HOLD.
      </p>

      <div>
        <label className="text-xs text-zinc-500 block mb-1">RESOLUTION STATE</label>
        <select
          value={resolutionState}
          onChange={e => setResolutionState(e.target.value)}
          className="w-full bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-2 py-1.5 text-xs"
          data-testid="resolution-state-select"
        >
          {RESOLUTION_STATE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-xs text-zinc-500 block mb-1">
          REVIEWER IDENTITY <span className="text-zinc-700">(preview identity)</span>
        </label>
        <input
          type="text"
          value={reviewer}
          onChange={e => setReviewer(e.target.value)}
          placeholder="e.g. Jordan Mercer"
          className="w-full bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-2 py-1.5 text-xs placeholder-zinc-700"
          data-testid="resolution-reviewer-input"
        />
      </div>

      <div>
        <label className="text-xs text-zinc-500 block mb-1">RESOLUTION NOTE (required)</label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Describe your resolution decision…"
          rows={2}
          className="w-full bg-zinc-900 border border-zinc-700 text-zinc-300 rounded px-2 py-1.5 text-xs placeholder-zinc-700 resize-y"
          data-testid="resolution-note-textarea"
        />
      </div>

      {errMsg && (
        <p className="text-xs text-red-400" data-testid="resolution-error">{errMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="px-3 py-1.5 text-xs rounded font-semibold transition-colors"
        style={{ backgroundColor: '#03f37c', color: '#000' }}
        data-testid="resolution-submit-btn"
      >
        {status === 'loading' ? 'Submitting…' : 'Submit Resolution'}
      </button>
    </form>
  );
}

// ─── EventCard with KS2-I3 resolution ────────────────────────────────────────

function EventCard({ event, onRefresh }) {
  const [expanded,     setExpanded]     = useState(false);
  const [showResolve,  setShowResolve]  = useState(false);
  const [liveState,    setLiveState]    = useState(event.review_state || 'Pending');

  const typeColor = EVENT_TYPE_COLORS[event.event_type] || 'bg-zinc-800 text-zinc-400';
  const priColor  = PRIORITY_COLORS[event.priority_heuristic] || 'text-zinc-500';
  const isResolved = liveState && liveState !== 'Pending';

  function handleResolved(result) {
    setLiveState(result.resulting_state);
    setShowResolve(false);
    if (onRefresh) onRefresh();
  }

  return (
    <div
      data-testid={`drift-event-${event.id}`}
      className={`border rounded-lg p-4 space-y-3 transition-colors ${
        isResolved ? 'border-zinc-700 opacity-75' : 'border-zinc-800 hover:border-zinc-700'
      }`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded font-mono ${typeColor}`} data-testid="event-type-badge">
          {event.event_type}
        </span>
        {event.priority_heuristic && (
          <span className={`text-xs font-mono ${priColor}`} data-testid="priority-heuristic-label">
            {event.priority_heuristic}
          </span>
        )}
        {isResolved && (
          <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-emerald-500 border border-zinc-700 font-mono" data-testid="resolution-state-badge">
            {liveState}
          </span>
        )}
        {event.is_synthetic_fixture && (
          <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400 border border-zinc-600 font-mono">
            SYNTHETIC DEMO
          </span>
        )}
        <span className="ml-auto text-xs text-zinc-600 font-mono">{event.passage_anchor}</span>
      </div>

      {/* Heuristic disclaimer */}
      {event.heuristic_basis && (
        <p className="text-xs text-zinc-600 border-l-2 border-zinc-700 pl-2" data-testid="heuristic-disclaimer">
          {event.heuristic_basis}
        </p>
      )}

      {/* Analytical note */}
      {event.analytical_note && (
        <p className="text-xs text-amber-700 italic">{event.analytical_note}</p>
      )}

      {/* Version provenance */}
      <div className="flex flex-wrap gap-4 text-xs text-zinc-600">
        <span>Baseline: <span className="font-mono text-zinc-400">{event.baseline_version_id}</span></span>
        <span>Target: <span className="font-mono text-zinc-400">{event.target_version_id}</span></span>
        <span>Mode: <span className="text-zinc-400">{event.comparison_mode}</span></span>
      </div>

      {/* Passage context toggle */}
      {(event.before_text || event.after_text || event.baseline_passage_text || event.target_passage_text) && (
        <button
          data-testid={`expand-event-${event.id}`}
          onClick={() => setExpanded(v => !v)}
          className="text-xs text-emerald-500 hover:text-emerald-400"
        >
          {expanded ? 'Hide passage context' : 'Show passage context'}
        </button>
      )}

      {expanded && (
        <div className="grid grid-cols-2 gap-3" data-testid="passage-context-expanded">
          <div className="space-y-1">
            <p className="text-xs font-medium text-zinc-500">Baseline</p>
            <div className="text-xs text-zinc-400 bg-zinc-900 rounded p-2 leading-relaxed border border-zinc-800">
              {event.before_text || event.baseline_passage_text || <span className="text-zinc-700 italic">(none)</span>}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium text-zinc-500">Target</p>
            <div className="text-xs text-zinc-400 bg-zinc-900 rounded p-2 leading-relaxed border border-zinc-800">
              {event.after_text || event.target_passage_text || <span className="text-zinc-700 italic">(none)</span>}
            </div>
          </div>
        </div>
      )}

      {event.constraint_keywords?.length > 0 && (
        <div className="text-xs text-zinc-600">
          Constraint keywords:{' '}
          <span className="text-amber-500">{event.constraint_keywords.join(', ')}</span>
        </div>
      )}

      {/* Resolution info if already resolved */}
      {event.resolved_by && (
        <div className="text-xs text-zinc-600 border-t border-zinc-800 pt-2" data-testid="resolution-info">
          Resolved by: <span className="text-zinc-400">{event.resolved_by}</span>
          {event.resolution_note && (
            <span className="ml-1 italic text-zinc-600">— "{event.resolution_note}"</span>
          )}
        </div>
      )}

      {/* Resolution toggle */}
      {!isResolved && (
        <div>
          <button
            onClick={() => setShowResolve(v => !v)}
            className="text-xs text-zinc-400 hover:text-emerald-400 transition-colors"
            data-testid={`toggle-resolve-${event.id}`}
          >
            {showResolve ? 'Cancel' : 'Initiate human review'}
          </button>

          {showResolve && (
            <ResolutionForm
              eventId={event.id}
              currentReviewState={liveState}
              onResolved={handleResolved}
            />
          )}
        </div>
      )}
    </div>
  );
}

function LegacyEventCard({ event }) {
  return (
    <div key={event.id} data-testid={`sentinel-event-${event.id}`}
      className="border border-zinc-800 rounded-lg p-4 space-y-1">
      <div className="flex gap-2 items-center">
        <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700 font-mono">
          {event.type}
        </span>
        <span className="text-sm text-zinc-300">{event.title}</span>
      </div>
      {event.description && (
        <p className="text-sm text-zinc-500">{event.description}</p>
      )}
      <p className="text-xs text-zinc-600">Source: <span className="text-zinc-500">{event.source}</span></p>
    </div>
  );
}

export default function Sentinel() {
  const [tab,          setTab]          = useState('drift');
  const [driftEvents,  setDriftEvents]  = useState([]);
  const [legacyEvents, setLegacyEvents] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [driftRes, legacyRes] = await Promise.all([
        api.get('/sentinel/drift-events'),
        api.get('/sentinel/events'),
      ]);
      setDriftEvents(driftRes.data.events || []);
      setLegacyEvents(legacyRes.data.events || []);
    } catch {
      setError('Failed to load sentinel data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const TABS = [
    { id: 'drift',   label: 'Drift Analysis',   count: driftEvents.length  },
    { id: 'events',  label: 'Sentinel Events',   count: legacyEvents.length },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="sentinel-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-100 font-['IBM_Plex_Sans']">Sentinel</h1>
        <p className="text-sm text-zinc-500 mt-1" data-testid="sentinel-governance-disclosure">
          Deterministic drift and conflict detection between governed source versions.
          All events are <strong className="text-zinc-400">candidate analytical findings</strong> — not institutional conclusions.
          A human reviewer must assess each event before any governance action is taken.
        </p>
        {/* KS2-I3 Constitutional disclosure */}
        <div className="mt-3 p-3 rounded border border-zinc-800 text-xs text-zinc-500" data-testid="sentinel-constitutional-disclosure">
          <strong className="text-zinc-400">Sentinel identifies, classifies, compares, and routes findings for review.
          Sentinel does not mutate canon or institutional authority.</strong>{' '}
          Human review is required before any governance action is taken.
          Reviewer identity is preview-supplied — KS-GATE-AUTH remains open.
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'text-emerald-400 border-b-2 border-emerald-500'
                : 'text-zinc-500 hover:text-zinc-300'
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-500">
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {loading && <div className="text-zinc-500 text-sm" data-testid="sentinel-loading">Loading sentinel data…</div>}
      {error && (
        <div className="text-red-400 text-sm border border-red-900 rounded p-3" data-testid="sentinel-error">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {tab === 'drift' && (
            <div className="space-y-3" data-testid="drift-tab-content">
              <div className="text-xs text-zinc-600 border border-zinc-800 rounded p-3">
                <strong className="text-zinc-400">Priority labels</strong> are a DETERMINISTIC PRIORITIZATION HEURISTIC
                derived mechanically from event type and constraint keyword presence.
                They are NOT a validated KYRDROS metric or institutional risk score.
              </div>
              {driftEvents.length === 0 ? (
                <p className="text-zinc-600 text-sm">No drift events recorded. Run a source-version comparison to detect changes.</p>
              ) : (
                driftEvents.map(ev => (
                  <EventCard key={ev.id} event={ev} onRefresh={load} />
                ))
              )}
            </div>
          )}

          {tab === 'events' && (
            <div className="space-y-3" data-testid="legacy-tab-content">
              {legacyEvents.length === 0 ? (
                <p className="text-zinc-600 text-sm">No sentinel events found.</p>
              ) : (
                legacyEvents.map(ev => <LegacyEventCard key={ev.id} event={ev} />)
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
